class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
(() => {
  const STOREFRONT_TOKEN = '9f1c7a7be20757fcaa68756e49182301';
  const API_URL = `https://{{ shop.permanent_domain }}/api/2024-07/graphql.json`;

  function moneyFmt(amount, currency, locale) {
    const n = Number(amount || 0);
    try { return new Intl.NumberFormat(locale || 'uk-UA', { style:'currency', currency }).format(n); }
    catch(_) { return n.toFixed(2) + ' ' + (currency || 'UAH'); }
  }

  async function fetchProductsByGIDs(gids) {
    if (!STOREFRONT_TOKEN || !Array.isArray(gids) || gids.length === 0) return [];
    const q = `
      query($ids:[ID!]!) {
        nodes(ids:$ids) {
          ... on Product {
            id handle title availableForSale onlineStoreUrl
            featuredImage { url altText }
            variants(first: 50) {
              edges { node { id title available price { amount currencyCode } } }
            }
          }
        }
      }`;
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type':'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        'Accept':'application/json'
      },
      body: JSON.stringify({ query: q, variables: { ids: gids } })
    });
    if (!res.ok) return [];
    const json = await res.json();
    return (json.data?.nodes || []).filter(Boolean);
  }

  function buildCards({ products, root }) {
    const grid = root.querySelector('[data-upsell-grid]');
    if (!grid) return;

    const limit = parseInt(root.getAttribute('data-upsell-limit') || '8', 10);
    const sliderFrom = parseInt(root.getAttribute('data-upsell-slider-from') || '3', 10);
    const cartIds = JSON.parse(root.getAttribute('data-upsell-cart') || '[]');
    const locale = root.getAttribute('data-upsell-locale') || 'uk-UA';
    const currency = root.getAttribute('data-upsell-currency') || 'UAH';

    const seen = new Set();
    const filtered = [];
    for (const p of products) {
      if (!p.availableForSale) continue;
      const pid = p.id.split('/').pop();
      if (cartIds.includes(Number(pid))) continue;
      if (seen.has(pid)) continue;
      seen.add(pid);
      filtered.push(p);
      if (filtered.length >= limit) break;
    }

    if (filtered.length === 0) {
      grid.innerHTML = `<small>(Upsell hint) У товаров в корзине не заполнен метафілд custom.related_products_list або він порожній.</small>`;
      return;
    }

    const isSlider = filtered.length >= sliderFrom;
    if (isSlider) {
      grid.outerHTML = `
        <div class="upsell-slider" data-upsell-slider>
          <button class="upsell-slider__nav upsell-slider__nav--prev" type="button" aria-label="Prev" data-upsell-prev>&lsaquo;</button>
          <div class="upsell-slider__track" data-upsell-track>${filtered.map(cardHTML).join('')}</div>
          <button class="upsell-slider__nav upsell-slider__nav--next" type="button" aria-label="Next" data-upsell-next>&rsaquo;</button>
        </div>`;
    } else {
      grid.innerHTML = filtered.map(cardHTML).join('');
    }

    function cardHTML(p) {
      const href = p.onlineStoreUrl || `/products/${p.handle}`;
      const img = p.featuredImage?.url || '';
      const variant = (p.variants?.edges || []).map(e => e.node).find(v => v.available) || (p.variants?.edges?.[0]?.node) || null;
      const vid = variant ? variant.id.split('/').pop() : '';
      const price = variant?.price?.amount ? moneyFmt(variant.price.amount, variant.price.currencyCode || currency, locale) : '';
      const wrap = isSlider ? 'upsell-card' : 'cart-upsell__item';
      const clsImg = isSlider ? 'upsell-card__img' : 'cart-upsell__img';
      const clsName = isSlider ? 'upsell-card__name' : 'cart-upsell__name';
      const clsPrice = isSlider ? 'upsell-card__price' : 'cart-upsell__price';
      const formCls = isSlider ? 'upsell-card__form' : 'cart-upsell__form';
      return `
        <div class="${wrap}">
          <a href="${href}" class="${clsImg}" aria-label="${escapeHTML(p.title)}">
            ${img ? `<img src="${img}&width=360" alt="${escapeHTML(p.title)}">` : ''}
          </a>
          <div class="${isSlider ? '' : 'cart-upsell__meta'}">
            <a href="${href}" class="${clsName}">${escapeHTML(p.title)}</a>
            <div class="${clsPrice}">${price}</div>
          </div>
          ${vid ? `
            <form class="${formCls}" data-upsell-form>
              <input type="hidden" name="id" value="${vid}">
              <input type="hidden" name="quantity" value="1">
              <button type="submit" class="button button--primary">{{ 'products.product.add_to_cart' | t }}</button>
            </form>
          ` : `<button type="button" class="button" disabled>{{ 'products.product.sold_out' | t }}</button>`}
        </div>`;
    }

    function escapeHTML(s){return String(s).replace(/[&<>"']/g,m=>({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[m]));}
  }

  async function mountUpsell() {
    const root = document.querySelector('[data-upsell-root]');
    if (!root) return;

    let gids = [];
    try { gids = JSON.parse(root.getAttribute('data-upsell-gids') || '[]'); } catch(_){}
    gids = Array.isArray(gids) ? gids.filter(Boolean) : [];

    if (!STOREFRONT_TOKEN || gids.length === 0) {
      const grid = root.querySelector('[data-upsell-grid]');
      if (grid) grid.innerHTML = `<small>(Upsell hint) У товаров в корзине не заполнен метафілд custom.related_products_list або він порожній.</small>`;
      return;
    }

    const products = await fetchProductsByGIDs(gids);
    buildCards({ products, root });
  }

  window.__upsellMount = mountUpsell; // чтобы вы могли дернуть руками из консоли

  document.addEventListener('DOMContentLoaded', mountUpsell);
  document.addEventListener('cart:updated', mountUpsell);

  document.addEventListener('click', async (e) => {
    const form = e.target.closest('form[data-upsell-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    try {
      const fd = new FormData(form);
      const addRes = await fetch('/cart/add.js', { method:'POST', headers:{ 'Accept':'application/json' }, body: fd });
      if (!addRes.ok) throw new Error('add failed');
      const res = await fetch('/cart?sections=cart-drawer,cart-icon-bubble', { headers:{ 'Accept':'text/html,application/json' } });
      const text = await res.text(); let data = {}; try{ data = JSON.parse(text);}catch(_){}
      const drawerContainer = document.querySelector('#CartDrawer');
      if (data['cart-drawer'] && drawerContainer) {
        const tmp = document.createElement('div'); tmp.innerHTML = data['cart-drawer'];
        const newInner = tmp.querySelector('#CartDrawer'); if (newInner) drawerContainer.innerHTML = newInner.innerHTML;
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (data['cart-icon-bubble'] && bubble) {
        const tmp2 = document.createElement('div'); tmp2.innerHTML = data['cart-icon-bubble'];
        const fresh = tmp2.querySelector('#cart-icon-bubble'); if (fresh) bubble.replaceWith(fresh);
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch(_) {
      alert('{{ "sections.cart.error" | t | default: "Помилка додавання" }}');
    } finally {
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
    }
  });

  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-upsell-prev], [data-upsell-prev] *')) {
      const root = e.target.closest('.upsell-slider'); const track = root?.querySelector('[data-upsell-track]');
      if (track) track.scrollBy({ left: -Math.max(240, track.clientWidth * 0.8), behavior: 'smooth' });
    }
    if (e.target.matches('[data-upsell-next], [data-upsell-next] *')) {
      const root = e.target.closest('.upsell-slider'); const track = root?.querySelector('[data-upsell-track]');
      if (track) track.scrollBy({ left:  Math.max(240, track.clientWidth * 0.8), behavior: 'smooth' });
    }
  });
})();
